import {
  CIO_SUBSCRIPTION_PREF_KEY,
  CREATED_IN_CIO_AT,
  IdentifierKey,
  IMMUTABLE_CUSTOMER_ATTRIBUTES,
} from './attribute-keys';
import { allEnabledIdentifiers, type IdentifiersConfig } from './identifiers';

/** The catalog shows at most this many names; the rest are reachable by searching. */
export const VISIBLE_ATTRIBUTE_LIMIT = 100;
/** The environment lists at most this many sent attributes; `more_attributes` flags an overflow. */
export const MAX_SEARCHABLE_ATTRIBUTES = 2000;

/**
 * The workspace's sent attributes that could still be added to a profile:
 * not an enabled identifier, not already on the profile, not system-owned, and
 * not the subscription-preferences JSON or any of its nested keys.
 */
export function availableAttributeNames(
  sentAttributes: readonly string[],
  identifiers: IdentifiersConfig | null | undefined,
  currentAttributeKeys: readonly string[],
): string[] {
  const rejected = new Set<string>([
    ...allEnabledIdentifiers(identifiers),
    ...currentAttributeKeys,
    ...IMMUTABLE_CUSTOMER_ATTRIBUTES,
    IdentifierKey.CioId,
    CREATED_IN_CIO_AT,
    CIO_SUBSCRIPTION_PREF_KEY,
  ]);
  return sentAttributes.filter(
    (attribute) => !rejected.has(attribute) && !attribute.startsWith(`${CIO_SUBSCRIPTION_PREF_KEY}.`),
  );
}

/** Substring match on the trimmed search term; a blank term matches everything. */
export function filterAttributeNames(names: readonly string[], search: string): string[] {
  const term = search.trim();
  if (term === '') {
    return [...names];
  }
  return names.filter((name) => name.includes(term));
}

export function attributeSearchPlaceholder(moreAttributes: boolean): string {
  return moreAttributes
    ? `Search your first ${MAX_SEARCHABLE_ATTRIBUTES.toLocaleString()} attributes`
    : 'Search for attribute';
}

type TrailerTextOptions = {
  visibleCount: number;
  filteredCount: number;
  /** The environment holds more sent attributes than it lists. */
  moreAttributes: boolean;
  /** No search term is narrowing the list, so the filtered count is the whole catalog. */
  allAvailableMatch: boolean;
  hasSearchTerm: boolean;
};

/** The line under a truncated list ("Showing 100 of 1,999+. Start your search."), or null when nothing is cut off. */
export function attributesTrailerText({
  visibleCount,
  filteredCount,
  moreAttributes,
  allAvailableMatch,
  hasSearchTerm,
}: TrailerTextOptions): string | null {
  if (visibleCount === filteredCount) {
    return null;
  }
  const total = `${filteredCount.toLocaleString()}${moreAttributes && allAvailableMatch ? '+' : ''}`;
  const prompt = hasSearchTerm ? 'Refine your search.' : 'Start your search.';
  return `Showing ${visibleCount.toLocaleString()} of ${total}. ${prompt}`;
}
